import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { ApiError } from './api';

type ToastKind = 'info' | 'success' | 'error';

interface Toast {
  id: number;
  kind: ToastKind;
  message: string;
}

interface ToastValue {
  notify: (message: string, kind?: ToastKind) => void;
  /** Surfaces an API failure without the caller having to unwrap it. */
  fail: (error: unknown, fallback?: string) => void;
}

const ToastContext = createContext<ToastValue | null>(null);

let nextId = 1;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const notify = useCallback((message: string, kind: ToastKind = 'info') => {
    const id = nextId++;
    setToasts((current) => [...current, { id, kind, message }]);
    setTimeout(() => {
      setToasts((current) => current.filter((toast) => toast.id !== id));
    }, kind === 'error' ? 6000 : 3500);
  }, []);

  const fail = useCallback(
    (error: unknown, fallback = 'Something went wrong. Please try again.') => {
      if (error instanceof ApiError) {
        const detail = error.details
          ? Object.values(error.details).flat().filter(Boolean).slice(0, 2).join(' ')
          : '';
        notify(detail ? `${error.message} ${detail}` : error.message, 'error');
      } else {
        notify(fallback, 'error');
      }
    },
    [notify],
  );

  const value = useMemo(() => ({ notify, fail }), [notify, fail]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {toasts.length > 0 && (
        <div className="toast-stack" role="status" aria-live="polite">
          {toasts.map((toast) => (
            <div key={toast.id} className={`toast ${toast.kind}`}>
              {toast.message}
            </div>
          ))}
        </div>
      )}
    </ToastContext.Provider>
  );
}

export function useToast(): ToastValue {
  const value = useContext(ToastContext);
  if (!value) throw new Error('useToast must be used inside ToastProvider');
  return value;
}
